import { TreeStatus } from "./TreeStatus";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import type { Tree } from "../data/types";
import { useLocalizedPath } from "../localization/urls";
import { PlaceLabel } from "./PlaceLabel";
import { photoSourceName, TreePhoto } from "./TreePhoto";
import "./CatalogueRings.css";

export const OPEN_DELAY = 150;
export const CLOSE_DELAY = 250;
const SWAP_DELAY = 80;

const SIZE = 440;
const CENTER = SIZE / 2;
const RING_RADII = [52, 86, 120, 154, 188];
/** Seconds per revolution; negative spins the ring the other way. */
const RING_SPIN = [320, -260, 200, -160, 130];

type RingDot = { tree: Tree; x: number; y: number };

/**
 * Trees are spread across rings proportional to radius, so the arc between
 * neighbouring dots stays roughly constant on every ring.
 */
function distributeAcrossRings(trees: Tree[]): RingDot[][] {
  const totalRadius = RING_RADII.reduce((sum, radius) => sum + radius, 0);
  const counts = RING_RADII.map((radius) => Math.floor((trees.length * radius) / totalRadius));
  let assigned = counts.reduce((sum, count) => sum + count, 0);
  for (let index = counts.length - 1; assigned < trees.length; index = (index - 1 + counts.length) % counts.length) {
    counts[index] += 1;
    assigned += 1;
  }

  let cursor = 0;
  return RING_RADII.map((radius, ringIndex) => {
    const ringTrees = trees.slice(cursor, cursor + counts[ringIndex]);
    cursor += counts[ringIndex];
    const phase = ringIndex * 0.67;
    return ringTrees.map((tree, index) => {
      const angle = phase + (Math.PI * 2 * index) / ringTrees.length;
      return {
        tree,
        x: CENTER + Math.cos(angle) * radius,
        y: CENTER + Math.sin(angle) * radius,
      };
    });
  });
}

type Active = { tree: Tree; ringIndex: number; anchor: Element };

export function CatalogueRings({
  trees,
  content,
}: {
  trees: Tree[];
  content: (key: string | null | undefined) => string;
}) {
  const { t } = useTranslation();
  const path = useLocalizedPath();
  const navigate = useNavigate();

  const stageRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<HTMLAnchorElement>(null);
  const openTimer = useRef<number | null>(null);
  const closeTimer = useRef<number | null>(null);
  const [active, setActive] = useState<Active | null>(null);
  const [anchorPoint, setAnchorPoint] = useState<{ x: number; y: number } | null>(null);

  const cancelOpen = () => {
    if (openTimer.current !== null) {
      window.clearTimeout(openTimer.current);
      openTimer.current = null;
    }
  };
  const cancelClose = () => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const activate = (next: Active) => {
    cancelOpen();
    cancelClose();
    setActive(next);
  };
  const scheduleOpen = (next: Active) => {
    cancelOpen();
    if (active?.tree.id === next.tree.id) {
      cancelClose();
      return;
    }
    openTimer.current = window.setTimeout(() => {
      openTimer.current = null;
      activate(next);
    }, active ? SWAP_DELAY : OPEN_DELAY);
  };
  const scheduleClose = () => {
    cancelOpen();
    cancelClose();
    closeTimer.current = window.setTimeout(() => {
      closeTimer.current = null;
      setActive(null);
    }, CLOSE_DELAY);
  };

  useEffect(() => () => {
    cancelOpen();
    cancelClose();
  }, []);

  useEffect(() => {
    if (!active) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      cancelOpen();
      cancelClose();
      setActive(null);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [active]);

  // The rings spin, so a dot's true position exists only at hover time: measure
  // it (after this render paused the ring) instead of trusting cx/cy.
  useLayoutEffect(() => {
    if (!active || !stageRef.current) {
      setAnchorPoint(null);
      return;
    }
    const dot = active.anchor.getBoundingClientRect();
    const stage = stageRef.current.getBoundingClientRect();
    setAnchorPoint({
      x: dot.left + dot.width / 2 - stage.left,
      y: dot.top + dot.height / 2 - stage.top,
    });
  }, [active]);

  // Flip below the dot when the card would leave the viewport top; shift
  // horizontally when it would leave either side.
  useLayoutEffect(() => {
    const card = previewRef.current;
    if (!card || !anchorPoint) return;
    const MARGIN = 16;
    card.classList.remove("below");
    let rect = card.getBoundingClientRect();
    if (rect.top < MARGIN) {
      card.classList.add("below");
      rect = card.getBoundingClientRect();
    }
    let shift = 0;
    if (rect.left < MARGIN) shift = MARGIN - rect.left;
    else if (rect.right > window.innerWidth - MARGIN) shift = window.innerWidth - MARGIN - rect.right;
    card.style.setProperty("--preview-shift", `${shift}px`);
  }, [anchorPoint, active]);

  const rings = distributeAcrossRings(trees);
  const previewPhoto = active?.tree.photos[0];

  return (
    <div className="catalogue-rings-stage" ref={stageRef}>
      <svg className="catalogue-rings" viewBox={`0 0 ${SIZE} ${SIZE}`} role="list" aria-label={t("count.catalogue")}>
        {RING_RADII.map((radius) => <circle className="catalogue-ring" key={radius} cx={CENTER} cy={CENTER} r={radius} />)}
        {rings.map((dots, ringIndex) => (
          <g
            key={RING_RADII[ringIndex]}
            className="catalogue-spin"
            data-paused={active?.ringIndex === ringIndex ? "true" : undefined}
            style={{
              animationDuration: `${Math.abs(RING_SPIN[ringIndex])}s`,
              animationDirection: RING_SPIN[ringIndex] < 0 ? "reverse" : "normal",
            }}
          >
            {dots.map(({ tree, x, y }) => {
              const to = path(`/tree/${tree.id}`);
              return (
                <a
                  key={tree.id}
                  href={to}
                  className="catalogue-dot-link"
                  aria-label={content(tree.keys.name)}
                  onClick={(event) => {
                    if (event.defaultPrevented || event.button !== 0) return;
                    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                    event.preventDefault();
                    navigate(to);
                  }}
                  onPointerEnter={(event) => scheduleOpen({ tree, ringIndex, anchor: event.currentTarget })}
                  onPointerLeave={scheduleClose}
                  onFocus={(event) => activate({ tree, ringIndex, anchor: event.currentTarget })}
                  onBlur={scheduleClose}
                >
                  <circle className="catalogue-dot-hit" cx={x} cy={y} aria-hidden="true" />
                  <circle
                    className={tree.status === "unconfirmed" ? "catalogue-dot catalogue-dot-unconfirmed" : "catalogue-dot"}
                    cx={x}
                    cy={y}
                    r="4.5"
                    aria-hidden="true"
                  />
                </a>
              );
            })}
          </g>
        ))}
        <text className="catalogue-rings-count" x={CENTER} y={CENTER - 2} aria-hidden="true">{trees.length}</text>
        <text className="catalogue-rings-label" x={CENTER} y={CENTER + 21} aria-hidden="true">{t("catalogue.title")}</text>
      </svg>

      {active && (
        <Link
          ref={previewRef}
          className="catalogue-preview"
          to={path(`/tree/${active.tree.id}`)}
          style={anchorPoint ? { left: anchorPoint.x, top: anchorPoint.y } : { visibility: "hidden" }}
          onPointerEnter={cancelClose}
          onPointerLeave={scheduleClose}
        >
          {previewPhoto && (
            <span className="catalogue-preview-photo">
              <TreePhoto photo={previewPhoto} alt="" target={480} sizes="240px" compactFallback />
            </span>
          )}
          {previewPhoto?.credit.kind === "external" && <span className="catalogue-preview-credit">{t("photo.creditVia", { host: photoSourceName(previewPhoto) })}</span>}
          <span className="catalogue-preview-name">{content(active.tree.keys.name)}</span>
          <span className="catalogue-preview-place"><PlaceLabel tree={active.tree} content={content} /></span>
          <TreeStatus tree={active.tree} />
        </Link>
      )}
    </div>
  );
}
